import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Upload, FileText, Loader2, Zap, Target, Sparkles } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useCredits } from "@/hooks/useCredits";
import { supabase } from "@/integrations/supabase/client";
import { DocumentUpload } from "@/components/DocumentUpload";
import { LinkedInWizardResults } from "@/components/LinkedInWizard/LinkedInWizardResults";
import { LinkedInProfileService } from "@/services/linkedinProfileService";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { generateLinkedInWordDocument, WordDocumentData } from "@/utils/linkedinDocumentUtils";

const LINKEDIN_WIZARD_CREDIT_COST = 5;

// Sanitize text to remove null bytes and control characters that PostgreSQL cannot store
const sanitizeText = (text: string): string => {
  if (!text) return '';
  
  return text
    // Remove null bytes
    .replace(/\u0000/g, '')
    // Remove control characters except newlines, carriage returns, and tabs
    .replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '')
    // Normalize Unicode to composed form
    .normalize('NFKC')
    // Trim whitespace
    .trim();
};

export default function LinkedInQuickWizard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { balance, deductCredits } = useCredits();
  
  const [step, setStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedProfile, setGeneratedProfile] = useState<any>(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [wordDocument, setWordDocument] = useState<WordDocumentData | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  
  // Form data
  const [uploadedDocuments, setUploadedDocuments] = useState<Array<{text: string, filename: string}>>([]);
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [targetJobTitle, setTargetJobTitle] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleDocumentUpload = (text: string, filename?: string) => {
    // Sanitize text to remove null bytes and control characters
    const sanitizedText = sanitizeText(text);
    
    console.log('Document upload - Original length:', text.length, 'Sanitized length:', sanitizedText.length);
    
    setUploadedDocuments(prev => [
      ...prev,
      { text: sanitizedText, filename: filename || "Document uploaded" }
    ]);
    toast({
      title: "Document uploaded!",
      description: `${filename || "Document"} has been processed successfully.`,
    });
  };

  const removeDocument = (index: number) => {
    setUploadedDocuments(prev => prev.filter((_, i) => i !== index));
  };

  // Combine all document text with sanitization
  const combinedResumeText = sanitizeText(
    uploadedDocuments.map(d => d.text).join('\n\n---\n\n')
  );

  const handleGenerate = async () => {
    if (uploadedDocuments.length === 0 || combinedResumeText.trim().length < 50) {
      toast({
        title: "Documents required",
        description: "Please upload at least one resume or document before generating.",
        variant: "destructive",
      });
      return;
    }

    if (!targetJobTitle.trim()) {
      toast({
        title: "Target job title required",
        description: "Please enter your target job title or position.",
        variant: "destructive",
      });
      return;
    }

    // Check credits
    if (user && balance < LINKEDIN_WIZARD_CREDIT_COST) {
      setShowUpgradePrompt(true);
      return;
    }

    setIsGenerating(true);

    // Sanitize all inputs
    const sanitizedJobTitle = sanitizeText(targetJobTitle);
    const sanitizedNotes = sanitizeText(additionalNotes);
    
    console.log('=== LINKEDIN GENERATION START ===');
    console.log('Combined text length:', combinedResumeText.length);
    console.log('Contains null bytes:', /\u0000/.test(combinedResumeText));

    try {
      // Try to save profile (optional - don't block generation if it fails)
      let savedProfileId: string | null = null;
      if (user) {
        try {
          const quickWizardData = {
            userCategories: ["professional"],
            primaryGoals: ["job_search"],
            uniqueSellingPoints: [],
            targetAudience: "Hiring managers and recruiters",
            whatYouHelp: "",
            howYouHelp: "",
            valueProposition: "",
            industries: [],
            audienceSeniority: "mid-senior",
            companySize: "any",
            experienceHighlights: [],
            resultsAndProof: "",
            voiceStyle: "professional",
            includeFaith: "no",
            keywords: sanitizedJobTitle,
            targetJobTitles: sanitizedJobTitle,
            pastJobTitles: "",
            skills: "",
            callToAction: "connect",
            uploadedFiles: [],
            pastedText: combinedResumeText,
            manuallyEnteredText: sanitizedNotes,
          };

          const savedProfile = await LinkedInProfileService.saveProfile(quickWizardData);
          if (savedProfile?.id) {
            savedProfileId = savedProfile.id;
            setProfileId(savedProfile.id);
          }
        } catch (profileError) {
          console.warn('Failed to save profile, continuing with generation:', profileError);
        }
      }

      // Call the edge function with only 3 inputs
      const { data: functionData, error: functionError } = await supabase.functions.invoke('generate-linkedin-profile', {
        body: {
          resumeText: combinedResumeText,
          jobTitle: sanitizedJobTitle,
          additionalInstructions: sanitizedNotes
        }
      });

      if (functionError) {
        throw new Error(functionError.message || 'Failed to generate LinkedIn profile');
      }

      if (!functionData?.success) {
        throw new Error(functionData?.error || 'LinkedIn profile generation failed');
      }

      const result = {
        headline: targetJobTitle,
        summary: functionData.generatedContent,
        experience: [],
        skills: [],
        keywords: [targetJobTitle],
        optimizationTips: [
          'Use a professional headshot as your profile photo',
          'Add industry-relevant skills to your skills section', 
          'Request recommendations from colleagues and clients',
          'Post regular content to increase visibility',
        ],
        rawContent: functionData.generatedContent,
        metadata: {
          generatedAt: new Date().toISOString(),
          source: 'openai_quick_mode',
          tokensUsed: functionData.usage?.total_tokens
        }
      };

      setGeneratedProfile(result);

      // Generate Word document via Docmosis (non-blocking)
      try {
        const wordDoc = await generateLinkedInWordDocument(
          functionData.generatedContent,
          sanitizedJobTitle
        );
        if (wordDoc) {
          setWordDocument(wordDoc);
          console.log('LinkedIn Word document generated successfully');
        }
      } catch (docError) {
        console.error('Failed to generate Word document:', docError);
        // Non-blocking - still show results even if doc generation fails
      }

      // Save generated profile to database so it appears in library
      if (user && savedProfileId) {
        try {
          const reportData = {
            generationDate: new Date().toISOString(),
            source: 'quick_wizard',
            targetJobTitle: sanitizedJobTitle
          };
          await LinkedInProfileService.saveGeneratedReport(
            savedProfileId,
            result,
            reportData,
            sanitizedJobTitle ? `${sanitizedJobTitle} Profile` : undefined
          );
          console.log('Generated profile saved to database');
        } catch (saveError) {
          console.warn('Failed to save generated profile:', saveError);
          // Non-blocking - profile still shows in results, just may not appear in library
        }
      }

      // Deduct credits
      if (user && savedProfileId) {
        await deductCredits('linkedin_profile_generation', { profileId: savedProfileId });
      }

      setStep(2);
      toast({
        title: "Profile generated!",
        description: "Your optimized LinkedIn profile is ready.",
      });

    } catch (error: any) {
      console.error('Generation error:', error);
      toast({
        title: "Generation failed",
        description: error.message || "Failed to generate profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Show results
  if (step === 2 && generatedProfile) {
    return (
      <LinkedInWizardResults 
        profile={generatedProfile}
        wordDocument={wordDocument}
        onBack={() => setStep(1)}
        onStartOver={() => {
          setGeneratedProfile(null);
          setWordDocument(null);
          setUploadedDocuments([]);
          setAdditionalNotes("");
          setTargetJobTitle("");
          setStep(1);
        }}
      />
    );
  }

  return (
    <>
      {showUpgradePrompt && (
        <UpgradePrompt
          feature="LinkedIn Profile Generation"
          creditsNeeded={LINKEDIN_WIZARD_CREDIT_COST}
          onDismiss={() => setShowUpgradePrompt(false)}
        />
      )}

      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="border-b border-border bg-card/50 backdrop-blur-sm px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/linkedin-wizard-landing')}
                className="h-8 w-8 p-0"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-500" />
                <h1 className="text-xl font-semibold text-foreground">Quick LinkedIn Profile Generator</h1>
              </div>
            </div>
            <div className="text-xs text-muted-foreground font-medium tracking-wide">
              powered by <span className="text-primary font-semibold">TailorWiz</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-6 py-8 max-w-3xl">
          {/* Hero */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-3 rounded-2xl shadow-lg">
                <Zap className="h-8 w-8 text-white" />
              </div>
            </div>
            <h2 className="text-3xl font-bold mb-2">60-Second LinkedIn Optimization</h2>
            <p className="text-muted-foreground">
              Upload your resume, tell us your target role, and get an optimized profile instantly
            </p>
          </div>

          {/* Form Card */}
          <Card className="shadow-xl border-2 border-amber-500/20">
            <CardHeader className="bg-gradient-to-r from-amber-500/10 to-orange-500/10">
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-600" />
                Quick LinkedIn Profile Generator
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Step 1: Document Uploads */}
              <div className="space-y-3">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <span className="bg-amber-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
                  Upload Your Resume(s) &amp; Documents
                </Label>
                <p className="text-sm text-muted-foreground">
                  Upload multiple documents for a more comprehensive profile optimization
                </p>
                <DocumentUpload
                  onUploadComplete={handleDocumentUpload}
                  title="Upload Documents"
                  description="PDF, DOC, DOCX, or TXT format - Upload multiple files"
                  acceptedTypes={['.pdf', '.doc', '.docx', '.txt']}
                />
                {uploadedDocuments.length > 0 && (
                  <div className="space-y-2">
                    {uploadedDocuments.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between gap-2 text-sm text-green-600 bg-green-50 p-2 rounded">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          <span>{doc.filename}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeDocument(index)}
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground">
                      {uploadedDocuments.length} document{uploadedDocuments.length !== 1 ? 's' : ''} uploaded
                    </p>
                  </div>
                )}
              </div>

              {/* Step 2: Target Job Title */}
              <div className="space-y-3">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <span className="bg-amber-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
                  Target Job Title or Position
                </Label>
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-muted-foreground" />
                  <Input
                    placeholder="e.g., Senior Product Manager, Marketing Director, Software Engineer"
                    value={targetJobTitle}
                    onChange={(e) => setTargetJobTitle(e.target.value)}
                    className="flex-1"
                  />
                </div>
              </div>

              {/* Step 3: Additional Notes (Optional) */}
              <div className="space-y-3">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <span className="bg-gray-400 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
                  Additional Notes (Optional)
                </Label>
                <Textarea
                  placeholder="Any special instructions, additional documents content, achievements, or specific things you want highlighted..."
                  value={additionalNotes}
                  onChange={(e) => setAdditionalNotes(e.target.value)}
                  className="min-h-[100px]"
                />
              </div>

              {/* Generate Button */}
              <Button
                onClick={handleGenerate}
                disabled={isGenerating || uploadedDocuments.length === 0 || !targetJobTitle.trim()}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white py-6 text-lg font-semibold"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Generating Your Optimized Profile...
                  </>
                ) : (
                  <>
                    <Zap className="h-5 w-5 mr-2" />
                    Generate Optimized LinkedIn Profile
                  </>
                )}
              </Button>

              {isGenerating && (
                <div className="space-y-2">
                  <Progress value={66} className="h-2" />
                  <p className="text-sm text-center text-muted-foreground">
                    AI is analyzing your resume and crafting your optimized profile...
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Back to Full Wizard Link */}
          <div className="text-center mt-6">
            <Button 
              variant="link" 
              onClick={() => navigate('/linkedin-wizard')}
              className="text-muted-foreground"
            >
              Want more customization? Try the full 12-step wizard →
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
